import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Package,
  Truck,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  MapPin,
  Activity,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  quoted:       { label: 'Quoted',       dot: 'bg-violet-500',  bg: 'bg-violet-500/10',  text: 'text-violet-600 dark:text-violet-400'    },
  posted:       { label: 'Posted',       dot: 'bg-purple-500',  bg: 'bg-purple-500/10',  text: 'text-purple-600 dark:text-purple-400'    },
  dispatched:   { label: 'Dispatched',   dot: 'bg-orange-500',  bg: 'bg-orange-500/10',  text: 'text-orange-600 dark:text-orange-400'    },
  picked_up:    { label: 'Picked Up',    dot: 'bg-indigo-500',  bg: 'bg-indigo-500/10',  text: 'text-indigo-600 dark:text-indigo-400'    },
  delivered:    { label: 'Delivered',    dot: 'bg-teal-500',    bg: 'bg-teal-500/10',    text: 'text-teal-600 dark:text-teal-400'        },
  cancelled:    { label: 'Cancelled',    dot: 'bg-red-500',     bg: 'bg-red-500/10',     text: 'text-red-600 dark:text-red-400'          },
};

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatDate() {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function StatCard({ label, value, sub, icon: Icon, gradient, iconBg, iconColor }) {
  return (
    <div className="relative bg-card border border-border rounded-2xl p-6 overflow-hidden shadow-sm">
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} pointer-events-none rounded-2xl`} />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p className="text-3xl font-outfit font-bold mt-2 text-foreground tracking-tight">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`h-11 w-11 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon className={`h-5 w-5 ${iconColor}`} strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentLoads, setRecentLoads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, loadsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/loads`,  { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (loadsRes.ok) setRecentLoads((await loadsRes.json()).slice(0, 6));
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const activeLoads = stats
    ? (stats.loads_by_status?.quoted || 0)
      + (stats.loads_by_status?.posted || 0)
      + (stats.loads_by_status?.dispatched || 0)
      + (stats.loads_by_status?.picked_up || 0)
    : null;

  
  const LoadInfoBadges = ({ listingId, loadNumber }) => (
  <div className="flex flex-wrap gap-2 mb-2">
    {/* Listing ID Badge (Blue Style) */}
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-sky-50 border border-sky-100 dark:bg-sky-500/10 dark:border-sky-500/20">
      <span className="text-[9px] font-bold uppercase tracking-wider text-sky-500 opacity-80">Listing ID</span>
      <span className="text-[11px] font-mono font-bold text-sky-700 dark:text-sky-400">{listingId || 'N/A'}</span>
    </div>

    {/* Load Number Badge (Red Style) */}
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-rose-50 border border-rose-100 dark:bg-rose-500/10 dark:border-rose-500/20">
      <span className="text-[9px] font-bold uppercase tracking-wider text-rose-500 opacity-80">Load ID</span>
      <span className="text-[11px] font-mono font-bold text-rose-700 dark:text-rose-400">{loadNumber || 'N/A'}</span>
    </div>
  </div>
);  

  return (
      <div className="space-y-6" data-testid="admin-dashboard">

        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-foreground">
              {greeting()}{user?.name ? `, ${user.name.split(' ')[0]}` : ''} 👋
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">{formatDate()} · Operations Overview</p>
          </div>
          <Link
            to="/admin/loads"
            className="hidden sm:flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-xl text-sm font-medium hover:bg-accent/90 transition-colors shrink-0"
          >
            View Loads <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            label="Active Loads"
            value={activeLoads ?? '—'}
            sub="In pipeline"
            icon={Package}
            gradient="from-red-500/8 to-transparent"
            iconBg="bg-red-500/10"
            iconColor="text-red-500"
          />
          <StatCard
            label="Total Deposits"
            value={stats ? `$${(stats.total_deposits || 0).toLocaleString()}` : '—'}
            sub="Collected revenue"
            icon={DollarSign}
            gradient="from-emerald-500/8 to-transparent"
            iconBg="bg-emerald-500/10"
            iconColor="text-emerald-600 dark:text-emerald-400"
          />
          <StatCard
            label="Active Carriers"
            value={stats?.carriers?.active ?? '—'}
            sub="Verified & ready"
            icon={Truck}
            gradient="from-blue-500/8 to-transparent"
            iconBg="bg-blue-500/10"
            iconColor="text-blue-600 dark:text-blue-400"
          />
        </div>

        {/* ── Loads by Status Boxes ── */}
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <Package className="h-4 w-4 text-accent" strokeWidth={1.5} />
            <h2 className="font-outfit font-semibold text-base text-foreground">Loads by Status</h2>
            {stats?.loads_by_status && (
              <span className="ml-auto text-xs text-muted-foreground">
                {Object.values(stats.loads_by_status).reduce((a, b) => a + b, 0)} total
              </span>
            )}
          </div>
          <div className="p-4 grid grid-cols-3 sm:grid-cols-6 gap-3">
            {Object.entries(STATUS_CONFIG).map(([status, cfg]) => {
              const count = stats?.loads_by_status?.[status] ?? 0;
              return (
                <Link
                  key={status}
                  to={`/admin/loads?status=${status}`}
                  className={`
                    group flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-200
                    ${count > 0
                      ? `${cfg.bg} border-transparent hover:scale-105 cursor-pointer`
                      : 'bg-muted/30 border-transparent opacity-50 cursor-default pointer-events-none'
                    }
                  `}
                >
                  <span className={`text-2xl font-outfit font-bold tabular-nums ${count > 0 ? cfg.text : 'text-muted-foreground'}`}>
                    {loading ? '—' : count}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${count > 0 ? cfg.dot : 'bg-muted-foreground/40'}`} />
                    <span className="text-[10px] font-medium text-muted-foreground leading-tight text-center">
                      {cfg.label}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* ── Pipeline + Recent Loads ── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Status Pipeline */}
          <div className="lg:col-span-2 bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center gap-2">
              <Activity className="h-4 w-4 text-accent" strokeWidth={1.5} />
              <h2 className="font-outfit font-semibold text-base text-foreground">Load Pipeline</h2>
            </div>
            <div className="p-4 space-y-2">
              {stats?.loads_by_status
                ? Object.entries(stats.loads_by_status)
                    .filter(([, count]) => count > 0)
                    .sort(([, a], [, b]) => b - a)
                    .map(([status, count]) => {
                      const cfg = STATUS_CONFIG[status] || { label: status, dot: 'bg-slate-400', bg: 'bg-slate-500/10', text: 'text-slate-500' };
                      const total = Object.values(stats.loads_by_status).reduce((a, b) => a + b, 0) || 1;
                      const pct = Math.round((count / total) * 100);
                      return (
                        <div key={status} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted/50 transition-colors">
                          <div className={`h-2 w-2 rounded-full ${cfg.dot} shrink-0`} />
                          <span className="text-sm text-foreground flex-1 capitalize">{cfg.label}</span>
                          <div className="flex items-center gap-3">
                            <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden hidden sm:block">
                              <div
                                className={`h-full ${cfg.dot} rounded-full transition-all duration-500`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className={`text-sm font-semibold tabular-nums w-6 text-right ${cfg.text}`}>{count}</span>
                          </div>
                        </div>
                      );
                    })
                : (
                  <div className="py-8 text-center text-muted-foreground text-sm">
                    {loading ? 'Loading…' : 'No data'}
                  </div>
                )
              }
            </div>
          </div>

          {/* Recent Loads */}
          <div className="lg:col-span-3 bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-accent" strokeWidth={1.5} />
                <h2 className="font-outfit font-semibold text-base text-foreground">Recent Loads</h2>
              </div>
              <Link to="/admin/loads" className="text-xs text-accent hover:text-accent/80 font-medium transition-colors flex items-center gap-1">
                See all <ArrowRight className="h-3 w-3" />
              </Link>
            </div>

            <div className="divide-y divide-border">
              {loading ? (
                <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
              ) : recentLoads.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-sm">No loads yet</div>
              ) : (
                recentLoads.map((load) => {
                  const cfg = STATUS_CONFIG[load.status] || STATUS_CONFIG.closed;
                  return (
                    <Link
                      key={load.id}
                      to={`/admin/loads/${load.id}`}
                      className="flex items-center gap-4 px-6 py-4 hover:bg-muted/40 transition-colors"
                      data-testid={`recent-load-${load.id}`}
                    >
                      {/* Vehicle */}
                      <div className="h-9 w-9 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                        <Package className="h-4 w-4 text-accent" strokeWidth={1.5} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {load.year} {load.vehicle_make} {load.vehicle_model}
                        </p>
                        <LoadInfoBadges 
                                  listingId={load.central_dispatch_listing_id} 
                                  loadNumber={load.cd_dispatch_snapshot?.loadNumber} 
                                />
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3 text-muted-foreground shrink-0" strokeWidth={1.5} />
                          <p className="text-xs text-muted-foreground truncate">
                            {load.pickup_zip} → {load.delivery_zip}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        {load.risk_flags?.length > 0 && (
                          <AlertTriangle className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
                        )}
                        <span className="text-sm font-bold text-foreground tabular-nums">
                          ${load.customer_price?.toFixed(0) ?? '—'}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
                          {cfg.label}
                        </span>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>
          </div>
        </div>

      </div>
  );
}
